import { useContext, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { 
  Box, 
  Button, 
  Chip, 
  Stack, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Tooltip,
  Paper,
  Badge,
} from '@mui/material';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ModeContext } from '../Context';
import { ThemeContext } from '../Context';
import { EditingContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { useIndiceInfos } from '../tool/GetIndiceInfos';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import ArticleIcon from '@mui/icons-material/Article';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'axios';
// Import de la fonction de téléchargement DOCX
import { downloadReportAsDocx } from './Download';
import History from './History';
import HomeQuickAccess from './HomeQuickAccess';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

function Up({ connectedUsers = [], totalConnectedUsers = 0 }) {
  const { report, pickReport } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { mode, handleModeChange } = useContext(ModeContext);
  const { theme } = useContext(ThemeContext);
  const { checkHasUnsavedChanges, resetEditingState } = useContext(EditingContext);
  const { getCompleteIndiceInfo } = useIndiceInfos();
  const muiTheme = useTheme();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const updateHeight = () => setHeaderHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, reportData, isEditorFullscreen]);

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);
  
  // États pour les statistiques des rubriques
  const [rubricCount, setRubricCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [connectedUsersDetails, setConnectedUsersDetails] = useState([]);

  const [downloadNotification, setDownloadNotification] = useState({
    open: false,
    severity: 'success',
    message: ''
  });

  // État pour le dialogue de confirmation de suppression
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    loading: false
  });

  // État pour le dialogue de confirmation de sortie
  const [exitDialog, setExitDialog] = useState({
    open: false
  });

  // Action différée en attente de confirmation (sortie, ouverture d'un autre document, etc.)
  const [pendingAction, setPendingAction] = useState(null);

  // État pour le drawer d'historique
  const [historyOpen, setHistoryOpen] = useState(false);

  // Fonction sécurisée pour changer de mode avec vérification
  const safeHandleModeChange = (newMode) => {
    if (handleModeChange && typeof handleModeChange === 'function') {
      const oldMode = mode;
      handleModeChange(newMode);
      
      // Log du changement de mode
      if (oldMode !== newMode) {
        logAction(LOG_ACTIONS.REPORT_MODE_CHANGE, formatReportNumber(report.number, report.year), {
          ancienMode: oldMode,
          nouveauMode: newMode
        });
      }
    } else {
      console.warn('handleModeChange n\'est pas disponible dans le contexte');
    }
  };

  // Charger les données du rapport
  useEffect(() => {
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        setReportData(response.data);
        
        // Calculer les statistiques des rubriques
        if (response.data.rubrics && response.data.rubrics.length > 0) {
          setRubricCount(response.data.rubrics.length);
          
          // Calculer le nombre de mots dans toutes les rubriques
          let totalWords = 0;
          response.data.rubrics.forEach(rubric => {
            // Compter les mots dans le titre
            if (rubric.title) {
              totalWords += rubric.title.trim().split(/\s+/).length;
            }
            
            // Compter les mots dans le contenu
            if (rubric.content) {
              totalWords += rubric.content.trim().split(/\s+/).length;
            }
          });
          
          setWordCount(totalWords);
        } else {
          setRubricCount(0);
          setWordCount(0);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du document:', error);
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Écouter les mises à jour via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year) {
        setReportData(prevData => ({
          ...prevData,
          ...updatedReport,
        }));
        
        // Mettre à jour les statistiques des rubriques si nécessaire
        if (updatedReport.rubrics) {
          setRubricCount(updatedReport.rubrics.length);
          
          // Calculer le nombre de mots dans toutes les rubriques
          let totalWords = 0;
          updatedReport.rubrics.forEach(rubric => {
            // Compter les mots dans le titre
            if (rubric.title) {
              totalWords += rubric.title.trim().split(/\s+/).length;
            }
            
            // Compter les mots dans le contenu
            if (rubric.content) {
              totalWords += rubric.content.trim().split(/\s+/).length;
            }
          });
          
          setWordCount(totalWords);
        }
      }
    };

    socket.on('reportUpdated', handleReportUpdated);

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
    };
  }, [socket, report]);

  // Récupère les infos (couleur, grade, nom) des utilisateurs connectés
  useEffect(() => {
    let isMounted = true;

    const fetchConnectedUsers = async () => {
      if (!connectedUsers || connectedUsers.length === 0) {
        if (isMounted) setConnectedUsersDetails([]);
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const results = await Promise.all(
          connectedUsers.map(async (matricule) => {
            try {
              const { data } = await axios.get(`/api/user/avatar/${matricule}`, { headers });
              return {
                matricule,
                color: data.nicknameColor || '#9e9e9e',
                rank: (data.rank || '').toUpperCase(),
                firstname: data.firstname || '',
                lastname: (data.lastname || '').toUpperCase(),
              };
            } catch (err) {
              console.error('Erreur lors de la récupération des infos utilisateur:', err);
              return {
                matricule,
                color: '#9e9e9e',
                rank: '...',
                firstname: '...',
                lastname: '',
              };
            }
          })
        );

        if (isMounted) {
          const uniqueByMatricule = results.filter(
            (user, index, self) => self.findIndex(u => u.matricule === user.matricule) === index
          );
          setConnectedUsersDetails(uniqueByMatricule);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs connectés:', error);
        if (isMounted) setConnectedUsersDetails([]);
      }
    };

    fetchConnectedUsers();

    return () => {
      isMounted = false;
    };
  }, [connectedUsers]);

  // Exécute une action de navigation en vérifiant d'éventuelles modifications non sauvegardées
  const handleNavigateWithGuard = (actionFn) => {
    if (typeof actionFn !== 'function') return;
    if (checkHasUnsavedChanges && checkHasUnsavedChanges()) {
      setPendingAction(() => actionFn);
      setExitDialog({ open: true });
    } else {
      resetEditingState();
      actionFn();
    }
  };

  const handleBack = () => {
    handleNavigateWithGuard(() => pickReport(null));
  };

  // Confirmer la sortie malgré les modifications en cours
  const handleConfirmExit = () => {
    resetEditingState();
    setExitDialog({ open: false });
    if (pendingAction) {
      const fn = pendingAction;
      setPendingAction(null);
      fn();
    } else {
      pickReport(null); // Retourne au Dashboard par défaut
    }
  };

  // Annuler la sortie
  const handleCancelExit = () => {
    setExitDialog({ open: false });
    setPendingAction(null);
  };

  // Fonction pour télécharger le rapport au format .docx
  const handleDownloadReport = async () => {
    if (!reportData) return;
    
    const result = await downloadReportAsDocx(reportData);
    
    setDownloadNotification({
      open: true,
      severity: result.success ? 'success' : 'error',
      message: result.message,
    });

    // Log du téléchargement
    if (result.success) {
      logAction(LOG_ACTIONS.REPORT_DOWNLOAD, formatReportNumber(report.number, report.year), {
        format: 'docx',
        rubricCount: rubricCount,
        wordCount: wordCount
      });
    }
  };

  // Fermer la notification de téléchargement
  const handleCloseDownloadNotification = () => {
    setDownloadNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Ouvrir l'historique des modifications
  const handleOpenHistory = () => {
    setHistoryOpen(true);
    // Log de l'ouverture de l'historique
    logAction(LOG_ACTIONS.HISTORY_VIEW, formatReportNumber(report.number, report.year), {
      indice: reportData?.indice || null
    });
  };

  // Ouvrir le dialogue de confirmation de suppression
  const handleDeleteClick = () => {
    setDeleteDialog({
      open: true,
      loading: false
    });
  };

  // Fermer le dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      loading: false
    });
  };

  // Confirmer et exécuter la suppression
  const handleConfirmDelete = async () => {
    if (!report) return;
    
    try {
      setDeleteDialog(prev => ({
        ...prev,
        loading: true
      }));

      const token = sessionStorage.getItem('authToken');
      await axios.delete(`/api/report/${report.number}/${report.year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Fermer le dialogue
      setDeleteDialog({
        open: false,
        loading: false
      });

      // Log de la suppression
      logAction(LOG_ACTIONS.REPORT_DELETE, formatReportNumber(report.number, report.year), {
        indice: reportData?.indice || null,
        unit: reportData?.unit || null
      });

      // Afficher une notification de succès
      setDownloadNotification({
        open: true,
        severity: 'success',
        message: 'Document supprimé avec succès'
      });

      // Retourner au dashboard après un délai
      setTimeout(() => {
        pickReport(null);
      }, 1500);

    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      
      setDeleteDialog({
        open: false,
        loading: false
      });

      // Afficher une notification d'erreur
      setDownloadNotification({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Erreur lors de la suppression du document'
      });
    }
  };

  if (loading || !reportData) {
    return <Box sx={{ p: 2 }}>Chargement...</Box>;
  }

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  const isRestricted = reportData.access?.type === 'restricted';
  const isDark = theme === 'dark';
  const showEmargementButton = false;
  const availableModes = showEmargementButton 
    ? ['emargement', 'edit', 'read'] 
    : ['edit', 'read'];
  const activeIndex = availableModes.includes(mode) 
    ? availableModes.indexOf(mode) 
    : 0;
  const contrastOnPrimary = muiTheme.palette.getContrastText(muiTheme.palette.primary.main);

  return (
    <Box
      ref={headerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '11px',
        paddingTop: '11px',
        paddingBottom: '11px',
        paddingLeft: headerHeight ? `${headerHeight + 11}px` : '11px',
        background: isDark 
          ? 'rgb(21, 34, 46)'
          : 'rgb(190, 214, 231)',
        position: 'relative',
      }}
    >
      {/* Bouton Maison - Cube pleine hauteur avec panneau d'accès rapide */}
      <HomeQuickAccess
        headerHeight={headerHeight}
        isDark={isDark}
        onHomeClick={handleBack}
        onNavigate={handleNavigateWithGuard}
      />

      {/* Partie gauche */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {(() => {
            const indiceCode = reportData?.indice || '__';
            const indiceInfo = getCompleteIndiceInfo(indiceCode);
            const IconComponent = indiceInfo.IconComponent;
            return (
              <IconComponent 
                sx={{ 
                  fontSize: 23, 
                  flexShrink: 0
                }} 
              />
            );
          })()}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '50vw',
              color: isDark ? '#ecf0f1' : 'inherit',
            }}
          >
            <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
            {reportData?.indice || '__'}.
            <SettingsFieldDisplay fieldName="judicialDivision" />.
            {report?.number}/{report?.year}
          </Typography>
        </Box>

        <Chip
          icon={isRestricted ? <LockIcon /> : <LockOpenIcon />}
          label={isRestricted ? "Restreint" : "Ouvert"}
          color={isRestricted ? "error" : "success"}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: '0.8rem',
            height: '24px',
            backgroundColor: isDark 
              ? (isRestricted ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)')
              : 'transparent',
            borderColor: isDark 
              ? (isRestricted ? 'rgba(244, 67, 54, 0.5)' : 'rgba(76, 175, 80, 0.5)')
              : undefined,
            color: isDark 
              ? (isRestricted ? '#ff6b6b' : '#4ecdc4')
              : undefined,
            '& .MuiChip-icon': {
              fontSize: '14px',
              color: 'inherit',
            },
            '& .MuiChip-label': {
              padding: '0 6px',
            }
          }}
        />
        
      </Stack>

      {/* Partie centrale parfaitement centrée */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: `repeat(${availableModes.length}, 1fr)`,
            gap: '4px',
            width: { xs: '78vw', sm: '440px' },
            borderRadius: '999px',
            px: '4px',
            py: '3px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)'}`,
            backdropFilter: 'saturate(160%) blur(8px)',
            boxShadow: isDark
              ? '0 8px 24px rgba(0,0,0,0.35)'
              : '0 6px 18px rgba(0,0,0,0.12)'
          }}
        >
          {/* Indicateur animé */}
          <Box
            sx={{
              position: 'absolute',
              top: 3,
              bottom: 3,
              left: 4,
              width: `calc((100% - 8px) / ${availableModes.length})`,
              borderRadius: '999px',
              background: `linear-gradient(135deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.primary.dark})`,
              transform: `translateX(${activeIndex * 100}%)`,
              transition: 'transform 220ms ease',
              boxShadow: isDark
                ? '0 8px 20px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)'
                : '0 6px 16px rgba(25,118,210,0.25)'
            }}
          />

          {showEmargementButton && (
            <Button
              onClick={() => safeHandleModeChange('emargement')}
              startIcon={<FilePresentIcon />}
              sx={{
                zIndex: 1,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: '34px',
                borderRadius: '999px',
                color: mode === 'emargement' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
                '&:hover': { backgroundColor: 'transparent' },
                minWidth: 0,
                whiteSpace: 'nowrap'
              }}
            >
              Émargement
            </Button>
          )}
          <Button
            onClick={() => safeHandleModeChange('edit')}
            startIcon={<EditIcon />}
            sx={{
              zIndex: 1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              height: '34px',
              borderRadius: '999px',
              color: mode === 'edit' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
              '&:hover': { backgroundColor: 'transparent' },
              minWidth: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Édition
          </Button>
          <Button
            onClick={() => safeHandleModeChange('read')}
            startIcon={<MenuBookIcon />}
            sx={{
              zIndex: 1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              height: '34px',
              borderRadius: '999px',
              color: mode === 'read' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
              '&:hover': { backgroundColor: 'transparent' },
              minWidth: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Lecture
          </Button>
        </Box>
      </Box>

      {/* Section droite - Statistiques et actions */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, justifyContent: 'flex-end' }}>
        {/* Statistiques */}
        <Stack direction="row" spacing={1}>
          <Tooltip title="Nombre de rubriques">
            <Chip
              icon={<ArticleIcon />}
              label={rubricCount}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: isDark 
                  ? 'rgba(66, 165, 245, 0.15)'
                  : 'rgba(25, 118, 210, 0.08)',
                borderColor: isDark 
                  ? 'rgba(66, 165, 245, 0.5)'
                  : 'rgba(25, 118, 210, 0.3)',
                color: isDark ? '#42a5f5' : '#1976d2',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: isDark ? '#42a5f5' : '#1976d2',
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Nombre de mots">
            <Chip
              icon={<TextFieldsIcon />}
              label={wordCount}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: isDark 
                  ? 'rgba(102, 187, 106, 0.15)'
                  : 'rgba(76, 175, 80, 0.08)',
                borderColor: isDark 
                  ? 'rgba(102, 187, 106, 0.5)'
                  : 'rgba(76, 175, 80, 0.3)',
                color: isDark ? '#66bb6a' : '#4caf50',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: isDark ? '#66bb6a' : '#4caf50',
                }
              }}
            />
          </Tooltip>
          <Tooltip 
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 1 }}>
                  Utilisateurs connectés
                </Typography>
                {connectedUsersDetails.length > 0 ? (
                  <Stack spacing={1}>
                    {connectedUsersDetails.map((user) => {
                      // Calcul des initiales
                      const initials = ((user.lastname?.[0] || '') + (user.firstname?.[0] || '')).toUpperCase() || '?';
                      
                      // Calcul couleur contrastée (simplifié)
                      const getTextColor = (bgColor) => {
                        if (!bgColor || bgColor === '#9e9e9e') return '#fff';
                        const hslMatch = bgColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
                        if (hslMatch) {
                          const h = parseInt(hslMatch[1]);
                          const complementH = (h + 180) % 360;
                          return `hsl(${complementH}, 80%, 20%)`;
                        }
                        return '#fff';
                      };
                      
                      return (
                        <Stack
                          key={user.matricule}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: user.color,
                              boxShadow: `0 0 3px 1px ${user.color}`,
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {user.rank} {user.lastname} {user.firstname}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic', opacity: 0.7 }}>
                    Aucun utilisateur connecté
                  </Typography>
                )}
              </Box>
            }
            placement="bottom"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(50, 50, 50, 0.95)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  p: 1.5,
                  minWidth: '200px'
                }
              },
              arrow: {
                sx: {
                  color: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(50, 50, 50, 0.95)'
                }
              }
            }}
          >
            <Chip
              icon={<PeopleIcon />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                  <span>{totalConnectedUsers}</span>
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, flexWrap: 'nowrap', gap: 0 }}>
                    {connectedUsersDetails.map((user, index) => (
                      <Box
                        key={user.matricule}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: user.color,
                          boxShadow: `0 0 3px 1px ${user.color}`,
                          marginLeft: index === 0 ? 0 : '-4px',
                          zIndex: connectedUsersDetails.length - index,
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            zIndex: 100
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              }
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: isDark 
                  ? 'rgba(255, 152, 0, 0.15)'
                  : 'rgba(255, 152, 0, 0.2)',
                borderColor: isDark 
                  ? 'rgba(255, 152, 0, 0.5)'
                  : 'rgba(255, 152, 0, 0.8)',
                color: isDark ? '#ffb74d' : '#e65100',
                fontWeight: 500,
                cursor: 'help',
                '& .MuiChip-icon': {
                  color: isDark ? '#ffb74d' : '#e65100',
                },
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  paddingRight: '2px',
                  whiteSpace: 'nowrap',
                },
                '&:hover': {
                  backgroundColor: isDark 
                    ? 'rgba(255, 152, 0, 0.25)'
                    : 'rgba(255, 152, 0, 0.3)',
                  borderColor: isDark 
                    ? 'rgba(255, 152, 0, 0.6)'
                    : 'rgba(255, 152, 0, 0.9)',
                }
              }}
            />
          </Tooltip>
        </Stack>

        <Divider orientation="vertical" flexItem />

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Tooltip title="Historique des modifications">
            <Button
              startIcon={<HistoryIcon />}
              onClick={handleOpenHistory}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                height: '32px',
                borderColor: isDark ? '#4fc3f7' : '#0288d1',
                color: isDark ? '#4fc3f7' : '#0288d1',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.05)' : 'transparent',
                '&:hover': {
                  backgroundColor: isDark ? '#4fc3f7' : '#0288d1',
                  color: 'white',
                  borderColor: isDark ? '#4fc3f7' : '#0288d1',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Historique
            </Button>
          </Tooltip>
          <Tooltip title="Supprimer le document">
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                height: '32px',
                borderColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                color: isDark ? '#f44336' : muiTheme.palette.error.main,
                backgroundColor: isDark ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                '&:hover': {
                  backgroundColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                  color: 'white',
                  borderColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                },
                transition: 'all 0.2s ease',
              }}
            >
              Supprimer
            </Button>
          </Tooltip>
          <Tooltip title="Télécharger en format Word">
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                height: '32px',
                borderColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                color: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                backgroundColor: isDark ? 'rgba(102, 187, 106, 0.05)' : 'transparent',
                '&:hover': {
                  backgroundColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                  color: 'white',
                  borderColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                },
                transition: 'all 0.2s ease',
              }}
            >
              Télécharger
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Notification de téléchargement */}
      <Snackbar 
        open={downloadNotification.open} 
        autoHideDuration={3000} 
        onClose={handleCloseDownloadNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseDownloadNotification} 
          severity={downloadNotification.severity}
          variant="filled"
        >
          {downloadNotification.message}
        </Alert>
      </Snackbar>

      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDark ? '#2d3748' : '#fff',
            color: isDark ? '#fff' : 'inherit',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: isDark ? '#ff6b6b' : muiTheme.palette.error.main,
            fontWeight: 'bold'
          }}
        >
          <DeleteIcon />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer définitivement ce document ?
          </Typography>
          <Typography variant="body2" sx={{ 
            fontWeight: 'bold',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
            {reportData?.indice || '__'}.
            <SettingsFieldDisplay fieldName="judicialDivision" />.
            {report?.number}/{report?.year}
          </Typography>
          <Typography variant="body2" sx={{ 
            mt: 2,
            color: isDark ? '#ff6b6b' : muiTheme.palette.error.main,
            fontStyle: 'italic'
          }}>
            Cette action est irréversible et supprimera toutes les données associées au document.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={deleteDialog.loading}
            sx={{
              borderColor: isDark ? '#4a5568' : muiTheme.palette.grey[400],
              color: isDark ? '#cbd5e0' : muiTheme.palette.text.secondary,
              '&:hover': {
                borderColor: isDark ? '#718096' : muiTheme.palette.grey[600],
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteDialog.loading}
            sx={{
              backgroundColor: isDark ? '#f44336' : muiTheme.palette.error.main,
              '&:hover': {
                backgroundColor: isDark ? '#d32f2f' : muiTheme.palette.error.dark,
              },
              '&:disabled': {
                backgroundColor: isDark ? '#4a4a4a' : muiTheme.palette.action.disabled,
              }
            }}
          >
            {deleteDialog.loading ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation de sortie avec modifications en cours */}
      <Dialog
        open={exitDialog.open}
        onClose={handleCancelExit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDark ? '#2d3748' : '#fff',
            color: isDark ? '#fff' : 'inherit',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: isDark ? '#ffb74d' : muiTheme.palette.warning.main,
            fontWeight: 'bold'
          }}
        >
          <ArrowBackIcon />
          Modifications en cours
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Vous avez des modifications ou des générations en cours qui seront perdues si vous quittez maintenant.
          </Typography>
          <Typography variant="body2" sx={{ 
            color: isDark ? '#ffb74d' : muiTheme.palette.warning.main,
            fontStyle: 'italic'
          }}>
            Voulez-vous vraiment quitter et perdre ces modifications ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelExit}
            variant="outlined"
            sx={{
              borderColor: isDark ? '#4a5568' : muiTheme.palette.grey[400],
              color: isDark ? '#cbd5e0' : muiTheme.palette.text.secondary,
              '&:hover': {
                borderColor: isDark ? '#718096' : muiTheme.palette.grey[600],
              }
            }}
          >
            Rester et continuer
          </Button>
          <Button
            onClick={handleConfirmExit}
            variant="contained"
            sx={{
              backgroundColor: isDark ? '#ff9800' : muiTheme.palette.warning.main,
              '&:hover': {
                backgroundColor: isDark ? '#f57c00' : muiTheme.palette.warning.dark,
              }
            }}
          >
            Quitter malgré tout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer Historique */}
      <History 
        open={historyOpen} 
        onClose={() => setHistoryOpen(false)} 
        reportData={reportData} 
      />
    </Box>
  );
}

export default Up;
